import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { canManagePolicyRules } from '../../helpers/authSession';
import { getActiveClientPolicySnapshot, getAdminOrganizations, getClientPolicies, getClientPolicyRules } from '../../services/complianceRules';
import { getRulesEngineUiMessage } from '../../services/rulesEngineErrors';
import { routePaths } from '../../config/navigation';
import RulesGroupAccordion from '../../components/RulesPlan/RulesGroupAccordion';

const COAF_GROUP_ORDER = [
  'I','II','III','IV','V','VI','VII','VIII','IX','X',
  'XI','XII','XIII','XIV','XV','XVI','XVII',
];

const groupRulesByCoafGroup = (rules) =>
  rules.reduce((groups, rule) => {
    const key = rule.coafGroup || 'Outros';
    if (!groups[key]) groups[key] = [];
    groups[key].push(rule);
    return groups;
  }, {});

const sortedGroupKeys = (rulesByGroup) =>
  Object.keys(rulesByGroup).sort((a, b) => {
    const ai = COAF_GROUP_ORDER.indexOf(a);
    const bi = COAF_GROUP_ORDER.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

const isActiveStatus = (status) => {
  const normalized = String(status || '').toLowerCase();
  return ['active', 'enabled', 'current', 'true', '1'].includes(normalized);
};

const isEditablePolicy = (policy) => {
  if (!policy || !policy.id) return false;
  if (!isActiveStatus(policy.status)) return false;

  const rawEffectiveTo = policy?.raw?.effective_to;
  if (!rawEffectiveTo) return true;

  const effectiveToDate = new Date(rawEffectiveTo);
  if (Number.isNaN(effectiveToDate.getTime())) return true;

  return effectiveToDate >= new Date();
};

const buildRiskEditPath = ({ selectedPolicyId, organizationId }) => {
  const query = new URLSearchParams();
  if (selectedPolicyId) query.set('policyId', selectedPolicyId);
  if (organizationId) query.set('organizationId', organizationId);
  const queryString = query.toString();
  return queryString ? `${routePaths.rulesPlanRiskEdit}?${queryString}` : routePaths.rulesPlanRiskEdit;
};

export default function RulesPlan() {
  const [loading, setLoading] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [policy, setPolicy] = React.useState(null);
  const [organizations, setOrganizations] = React.useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = React.useState('');
  const [policies, setPolicies] = React.useState([]);
  const [selectedPolicyId, setSelectedPolicyId] = React.useState('');
  const [rules, setRules] = React.useState([]);
  const navigate = useNavigate();
  const canManageGovernance = canManagePolicyRules();
  const canManageRules = canManagePolicyRules();

  const resolvedOrganizationId = canManageGovernance ? selectedOrganizationId : '';

  const organizationNameById = React.useMemo(() => organizations.reduce((acc, organization) => {
    acc[organization.id] = organization.name;
    return acc;
  }, {}), [organizations]);

  React.useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setErrorMessage('');
        const [snapshot, availablePolicies, organizationsResponse] = await Promise.all([
          getActiveClientPolicySnapshot({ scopeType: 'organization', organizationId: resolvedOrganizationId }),
          getClientPolicies({ scopeType: 'organization', organizationId: resolvedOrganizationId }),
          canManageGovernance ? getAdminOrganizations() : Promise.resolve([]),
        ]);
        if (!isMounted) return;

        setOrganizations(organizationsResponse);
        if (canManageGovernance && !selectedOrganizationId && organizationsResponse.length) {
          setSelectedOrganizationId(organizationsResponse[0].id);
        }

        const normalizedPolicies = [...availablePolicies]
          .filter((item) => item.scopeType === 'organization')
          .filter((item) => {
            if (!canManageGovernance || !selectedOrganizationId) {
              return true;
            }

            return item.organizationId === selectedOrganizationId;
          })
          .filter((item) => isEditablePolicy(item));

        const snapshotPolicy = snapshot?.policy
          && snapshot.policy.scopeType === 'organization'
          && (!canManageGovernance || !selectedOrganizationId || snapshot.policy.organizationId === selectedOrganizationId)
          && isEditablePolicy(snapshot.policy)
          ? snapshot.policy
          : null;

        const mergedPolicies = [...normalizedPolicies];
        if (snapshotPolicy?.id && !mergedPolicies.some((item) => item.id === snapshotPolicy.id)) {
          mergedPolicies.unshift(snapshotPolicy);
        }

        setPolicies(mergedPolicies);

        const initialPolicyId = snapshotPolicy?.id
          ? snapshotPolicy.id
          : mergedPolicies[0]?.id || '';

        setSelectedPolicyId(initialPolicyId);

        if (!initialPolicyId) {
          setPolicy(null);
          setRules([]);
          return;
        }

        const initialPolicy = mergedPolicies.find((item) => item.id === initialPolicyId) || snapshotPolicy || null;
        setPolicy(initialPolicy);
        setRules(snapshotPolicy?.id === initialPolicyId ? snapshot.rules : []);
      } catch (error) {
        if (isMounted) {
          setPolicies([]);
          setSelectedPolicyId('');
          setPolicy(null);
          setRules([]);
          setOrganizations([]);
          setErrorMessage(getRulesEngineUiMessage(error));
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();
    return () => { isMounted = false; };
  }, [canManageGovernance, resolvedOrganizationId, selectedOrganizationId]);

  React.useEffect(() => {
    if (!selectedPolicyId) return;
    if (policy?.id === selectedPolicyId && rules.length) return;

    let isMounted = true;

    const loadSelectedPolicyRules = async () => {
      try {
        setLoading(true);
        setErrorMessage('');

        const selectedPolicy = policies.find((item) => item.id === selectedPolicyId) || null;
        const selectedPolicyRules = await getClientPolicyRules(selectedPolicyId, { organizationId: resolvedOrganizationId });
        if (!isMounted) return;

        setPolicy(selectedPolicy);
        setRules(selectedPolicyRules);
      } catch (error) {
        if (!isMounted) return;
        setRules([]);
        setErrorMessage(getRulesEngineUiMessage(error));
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadSelectedPolicyRules();
    return () => { isMounted = false; };
  }, [policies, policy, rules.length, selectedPolicyId, resolvedOrganizationId]);

  const rulesByGroup = React.useMemo(() => groupRulesByCoafGroup(rules), [rules]);

  const selectedPolicyOrganizationId = React.useMemo(() => {
    const selectedPolicy = policies.find((item) => item.id === selectedPolicyId) || policy || null;
    return selectedPolicy?.organizationId || resolvedOrganizationId || '';
  }, [policies, policy, selectedPolicyId, resolvedOrganizationId]);

  const handleEditGroup = React.useCallback(
    (group) => {
      const basePath = routePaths.rulesPlanGroupEdit.replace(':group', group);
      const query = new URLSearchParams();
      if (selectedPolicyId) query.set('policyId', selectedPolicyId);
      if (selectedPolicyOrganizationId) query.set('organizationId', selectedPolicyOrganizationId);
      const queryString = query.toString();
      const destination = queryString ? `${basePath}?${queryString}` : basePath;
      navigate(destination);
    },
    [navigate, selectedPolicyId, selectedPolicyOrganizationId],
  );

  const handleEditGlobalRisk = React.useCallback(() => {
    navigate(buildRiskEditPath({ selectedPolicyId, organizationId: selectedPolicyOrganizationId }));
  }, [navigate, selectedPolicyId, selectedPolicyOrganizationId]);

  if (loading) {
    return (
      <Card variant="outlined" sx={{ width: '100%' }}>
        <CardContent>
          <Stack sx={{ alignItems: 'center', py: 6 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
              Carregando regras da policy...
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Stack spacing={2}>
        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1}>
              <Typography component="h1" variant="h5">Regras da Policy</Typography>
              {canManageGovernance ? (
                <FormControl size="small" sx={{ maxWidth: 480 }}>
                  <InputLabel id="rules-plan-organization-select-label">Organização-alvo</InputLabel>
                  <Select
                    labelId="rules-plan-organization-select-label"
                    label="Organização-alvo"
                    value={selectedOrganizationId}
                    onChange={(event) => setSelectedOrganizationId(event.target.value)}
                  >
                    {organizations.map((organization) => (
                      <MenuItem key={organization.id} value={organization.id}>{organization.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
              {policies.length ? (
                <FormControl size="small" sx={{ maxWidth: 360 }}>
                  <InputLabel id="rules-plan-select-label">Política para edição</InputLabel>
                  <Select
                    labelId="rules-plan-select-label"
                    label="Política para edição"
                    value={selectedPolicyId}
                    onChange={(event) => setSelectedPolicyId(event.target.value)}
                  >
                    {policies.map((item) => (
                      <MenuItem key={item.id} value={item.id}>{`${item.name} v${item.version}`}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
              {policy ? (
                <>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Política: {policy.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Versão: {policy.version}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Cobertura: {policy.coverage}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Organização: {organizationNameById[policy.organizationId] || '-'}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Nenhuma política ativa encontrada no momento.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <Stack spacing={0.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Configuração global da policy
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Classificação de risco aplicada de forma transversal, fora dos grupos regulatórios.
                </Typography>
              </Stack>
              {canManageRules ? (
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  disableRipple
                  onClick={handleEditGlobalRisk}
                >
                  Editar risco global
                </Button>
              ) : null}
            </Stack>
          </CardContent>
        </Card>

        {sortedGroupKeys(rulesByGroup).map((group) => (
          <RulesGroupAccordion
            key={group}
            group={group}
            rules={rulesByGroup[group]}
            onEditGroup={handleEditGroup}
            canManage={canManageRules}
          />
        ))}

        {!rules.length && !errorMessage ? (
          <Alert severity="info">Nenhuma regra encontrada para a política atual.</Alert>
        ) : null}
      </Stack>
    </Box>
  );
}
